import { FC, forwardRef, ReactNode, RefObject, useEffect, useState } from 'react';

import { Box, Button, Flex, HStack, IconButton, Popover, PopoverContent, PopoverTrigger, Portal, rem, Tooltip } from '@bonusly/design-system';

import { DOODLE_COLORS, DOODLE_ERASER_SIZES, DOODLE_SIZES, type DoodleTranslate, type SelectableDoodleTool } from './doodle-config';
import { DoodleCustomColorPicker } from './doodle-custom-color-picker';
import { EraserIcon, MARKER_ICONS } from './doodle-tool-icons';

const CUSTOM_COLOR_STORAGE_KEY = 'bonusly-doodle-custom-colors';
const MAX_CUSTOM_COLORS = 10;

const normalizeColor = (color: string): string => color.toUpperCase();
const isHexColor = (value: unknown): value is string => typeof value === 'string' && /^#[0-9A-F]{6}$/i.test(value);

const loadCustomColors = (): string[] => {
	try {
		const stored: unknown = JSON.parse(window.localStorage.getItem(CUSTOM_COLOR_STORAGE_KEY) ?? '[]');
		if (!Array.isArray(stored)) return [];
		return [...new Set(stored.filter(isHexColor).map(normalizeColor))].slice(0, MAX_CUSTOM_COLORS);
	} catch {
		return [];
	}
};

interface ToolButtonBaseProps {
	label: string;
	isActive: boolean;
	onClick: () => void;
	children: ReactNode;
	width: number;
}

const ToolButtonBase = forwardRef<HTMLButtonElement, ToolButtonBaseProps>(({ label, isActive, onClick, children, width, ...rest }, ref) => (
	<Box
		ref={ref}
		as="button"
		type="button"
		aria-label={label}
		aria-pressed={isActive}
		position="relative"
		w={{ base: rem(width * 0.82), sm: rem(width) }}
		minW={{ base: rem((width - 16) * 0.84), sm: rem(width - 16) }}
		h={rem(72)}
		flexShrink={1}
		bg="transparent"
		border={0}
		cursor="pointer"
		_focusVisible={{ outline: '2px solid', outlineColor: 'primary.fg', outlineOffset: rem(-3), borderRadius: 'sm' }}
		sx={{
			'& > span': {
				filter: isActive ? 'drop-shadow(0 5px 5px rgb(18 25 38 / 24%))' : 'drop-shadow(0 2px 2px rgb(18 25 38 / 8%))',
				transform: `translate(-50%, ${isActive ? rem(-30) : '0'})`,
			},
			'&:hover > span': {
				filter: 'drop-shadow(0 5px 5px rgb(18 25 38 / 20%))',
				transform: `translate(-50%, ${isActive ? rem(-30) : rem(-12)})`,
			},
			'&:active > span': {
				filter: 'drop-shadow(0 2px 2px rgb(18 25 38 / 14%))',
				transform: `translate(-50%, ${isActive ? rem(-26) : rem(-6)}) scale(0.97)`,
			},
		}}
		onClick={onClick}
		{...rest}
	>
		<Box
			as="span"
			position="absolute"
			top={rem(44)}
			left="50%"
			display="block"
			lineHeight={0}
			transition="transform 180ms cubic-bezier(0.16, 1, 0.3, 1), filter 180ms cubic-bezier(0.16, 1, 0.3, 1)"
		>
			<Box as="span" display="block" transform={{ base: 'scale(0.84)', sm: 'scale(1)' }} transformOrigin="top center">
				{children}
			</Box>
		</Box>
	</Box>
));

ToolButtonBase.displayName = 'ToolButtonBase';

type DoodleToolButtonProps = ToolButtonBaseProps;

const DoodleToolButton: FC<DoodleToolButtonProps> = (props) => (
	<Tooltip label={props.label} placement="top">
		<ToolButtonBase {...props} />
	</Tooltip>
);

const DoubleDivider: FC = () => (
	<Flex width={rem(2)} height={rem(51)} flexShrink={0} aria-hidden>
		<Box width={rem(1)} bg="border.default" />
		<Box width={rem(1)} bg="bg.surface" />
	</Flex>
);

interface ColorSwatchProps {
	color: string;
	label: string;
	isSelected: boolean;
	onClick: () => void;
}

const ColorSwatch: FC<ColorSwatchProps> = ({ color, label, isSelected, onClick }) => (
	<Tooltip label={label} placement="top">
		<Box
			as="button"
			type="button"
			aria-label={label}
			aria-pressed={isSelected}
			width={rem(36)}
			height={rem(36)}
			p={rem(2)}
			borderRadius="full"
			bg={isSelected ? 'primary.fg' : 'transparent'}
			border={0}
			cursor="pointer"
			transition="transform 150ms cubic-bezier(0.16, 1, 0.3, 1)"
			_hover={{ transform: 'translateY(-2px)' }}
			_focusVisible={{ outline: '2px solid', outlineColor: 'primary.fg', outlineOffset: rem(2) }}
			onClick={onClick}
		>
			<Box
				width="100%"
				height="100%"
				borderRadius="full"
				bg={color}
				borderWidth={rem(2)}
				borderStyle="solid"
				borderColor={isSelected ? 'bg.surface' : 'transparent'}
			/>
		</Box>
	</Tooltip>
);

export interface DoodleToolbarProps {
	tool: SelectableDoodleTool | 'fill';
	color: string;
	size: number;
	eraserSize: number;
	isDrawing: boolean;
	canUndo: boolean;
	canRedo: boolean;
	onToolChange: (tool: SelectableDoodleTool) => void;
	onColorChange: (color: string) => void;
	onSizeChange: (size: number) => void;
	onEraserSizeChange: (size: number) => void;
	onUndo: () => void;
	onRedo: () => void;
	onCancel: () => void;
	onSave: () => void;
	isSaveDisabled: boolean;
	isSaving?: boolean;
	translate: DoodleTranslate;
	saveLabel: string;
	portalContainerRef?: RefObject<HTMLElement>;
}

const UndoIcon: FC = () => (
	<svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
		<path d="M7.5 11.6667L3.33333 7.50002L7.5 3.33335" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
		<path
			d="M16.6666 16.6667V10.8334C16.6666 9.9493 16.3154 9.10145 15.6903 8.47633C15.0652 7.85121 14.2173 7.50002 13.3333 7.50002H3.33329"
			stroke="currentColor"
			strokeWidth="1.5"
			strokeLinecap="round"
			strokeLinejoin="round"
		/>
	</svg>
);

const RedoIcon: FC = () => (
	<svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
		<g transform="translate(20 0) scale(-1 1)">
			<path d="M7.5 11.6667L3.33333 7.50002L7.5 3.33335" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
			<path
				d="M16.6666 16.6667V10.8334C16.6666 9.9493 16.3154 9.10145 15.6903 8.47633C15.0652 7.85121 14.2173 7.50002 13.3333 7.50002H3.33329"
				stroke="currentColor"
				strokeWidth="1.5"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
		</g>
	</svg>
);

export const DoodleToolbar: FC<DoodleToolbarProps> = ({
	tool,
	color,
	size,
	eraserSize,
	isDrawing,
	canUndo,
	canRedo,
	onToolChange,
	onColorChange,
	onSizeChange,
	onEraserSizeChange,
	onUndo,
	onRedo,
	onCancel,
	onSave,
	isSaveDisabled,
	isSaving = false,
	translate: t,
	saveLabel,
	portalContainerRef,
}) => {
	const [customColors, setCustomColors] = useState<string[]>(loadCustomColors);
	const [isCustomPickerOpen, setIsCustomPickerOpen] = useState(false);
	const [isEraserMenuOpen, setIsEraserMenuOpen] = useState(false);

	// The eraser size menu is dismissed the moment a stroke starts (the user
	// began erasing) or whenever a different tool takes over.
	useEffect(() => {
		if (isDrawing || tool !== 'eraser') setIsEraserMenuOpen(false);
	}, [isDrawing, tool]);

	useEffect(() => {
		try {
			window.localStorage.setItem(CUSTOM_COLOR_STORAGE_KEY, JSON.stringify(customColors));
		} catch {
			// Custom colors still work for this session when storage is unavailable.
		}
	}, [customColors]);

	const chooseColor = (nextColor: string, closePalette?: () => void) => {
		onColorChange(normalizeColor(nextColor));
		closePalette?.();
	};

	const addCustomColor = (colorToAdd: string) => {
		const nextColor = normalizeColor(colorToAdd);
		if (!DOODLE_COLORS.some((preset) => preset.hex === nextColor)) {
			setCustomColors((current) => [nextColor, ...current.filter((storedColor) => storedColor !== nextColor)].slice(0, MAX_CUSTOM_COLORS));
		}
		onColorChange(nextColor);
	};

	const deleteCustomColor = (deletedColor: string) => {
		setCustomColors((current) => current.filter((storedColor) => storedColor !== deletedColor));
		if (color === deletedColor) onColorChange(DOODLE_COLORS[0].hex);
	};

	return (
		<Flex
			position="relative"
			minHeight={rem(72)}
			bg="bg.subtle"
			borderTopWidth={rem(1)}
			borderTopStyle="solid"
			borderColor="border.default"
			boxShadow="foot-md"
			px={{ base: 2, sm: 4 }}
			gap={{ base: 1, sm: 3 }}
			align="center"
			justify="space-between"
			flexWrap={{ base: 'wrap', sm: 'nowrap' }}
			overflow="hidden"
			sx={{
				'@keyframes doodleToolbarIn': {
					'0%': { opacity: 0, transform: 'translateY(8px)' },
					'100%': { opacity: 1, transform: 'translateY(0)' },
				},
				animation: 'doodleToolbarIn 220ms cubic-bezier(0.16, 1, 0.3, 1)',
				'@media (prefers-reduced-motion: reduce)': { animation: 'none' },
			}}
		>
			<Flex
				height={rem(72)}
				align="center"
				gap={{ base: 2, sm: 4 }}
				minWidth={0}
				width={{ base: '100%', sm: 'auto' }}
				overflowX={{ base: 'auto', sm: 'visible' }}
			>
				<Popover placement="top-start" isLazy onClose={() => setIsCustomPickerOpen(false)}>
					{({ isOpen, onClose }) => (
						<>
							<PopoverTrigger>
								<Box
									as="button"
									type="button"
									aria-label={t('doodle.color_palette')}
									width={{ base: rem(48), sm: rem(56) }}
									height={{ base: rem(48), sm: rem(56) }}
									minWidth={{ base: rem(48), sm: rem(56) }}
									maxWidth={{ base: rem(48), sm: rem(56) }}
									flexShrink={0}
									aspectRatio={1}
									display="flex"
									alignItems="center"
									justifyContent="center"
									p={rem(2)}
									borderRadius="full"
									bg={isOpen ? 'primary.fg' : 'transparent'}
									border={0}
									cursor="pointer"
									transition="transform 180ms cubic-bezier(0.16, 1, 0.3, 1)"
									_hover={{ transform: 'translateY(-2px)' }}
									_focusVisible={{ outline: '2px solid', outlineColor: 'primary.fg', outlineOffset: rem(2) }}
								>
									<Box
										key={color}
										width="100%"
										height="100%"
										aspectRatio={1}
										borderRadius="full"
										bg={color}
										borderWidth={rem(2)}
										borderStyle="solid"
										borderColor="bg.surface"
										sx={{
											'@keyframes doodleColorPop': {
												'0%': { transform: 'scale(0.8)' },
												'65%': { transform: 'scale(1.06)' },
												'100%': { transform: 'scale(1)' },
											},
											animation: 'doodleColorPop 220ms cubic-bezier(0.16, 1, 0.3, 1)',
											'@media (prefers-reduced-motion: reduce)': { animation: 'none' },
										}}
									/>
								</Box>
							</PopoverTrigger>
							<Portal containerRef={portalContainerRef}>
								<PopoverContent
									width={isCustomPickerOpen ? rem(344) : rem(267)}
									p={3}
									bg="bg.surface"
									borderColor="border.soft"
									borderRadius="xl"
									boxShadow="xl"
									_focusVisible={{ boxShadow: 'xl' }}
								>
									{isCustomPickerOpen ? (
										<DoodleCustomColorPicker
											initialColor={color}
											translate={t}
											onCancel={() => setIsCustomPickerOpen(false)}
											onAdd={(nextColor) => {
												addCustomColor(nextColor);
												setIsCustomPickerOpen(false);
												onClose();
											}}
										/>
									) : (
										<Box display="grid" gridTemplateColumns={`repeat(5, ${rem(36)})`} gap={3}>
											{DOODLE_COLORS.map((preset) => (
												<ColorSwatch
													key={preset.hex}
													color={preset.hex}
													label={t(`doodle.colors.${preset.name}`)}
													isSelected={color === preset.hex}
													onClick={() => chooseColor(preset.hex, onClose)}
												/>
											))}
											{customColors.map((customColor) => (
												<Box key={customColor} role="group" position="relative" width={rem(36)} height={rem(36)}>
													<ColorSwatch
														color={customColor}
														label={t('doodle.colors.custom', { color: customColor })}
														isSelected={color === customColor}
														onClick={() => chooseColor(customColor, onClose)}
													/>
													<Box
														as="button"
														type="button"
														aria-label={t('doodle.colors.delete', { color: customColor })}
														position="absolute"
														top={rem(-3)}
														right={rem(-3)}
														width={rem(16)}
														height={rem(16)}
														borderRadius="full"
														bg="secondary.fg"
														color="text.onColor"
														fontSize={rem(12)}
														lineHeight={1}
														opacity={0}
														borderWidth={rem(1)}
														borderStyle="solid"
														borderColor="bg.surface"
														cursor="pointer"
														_groupHover={{ opacity: 1 }}
														_focusVisible={{ opacity: 1, outline: '2px solid', outlineColor: 'primary.fg' }}
														onClick={() => deleteCustomColor(customColor)}
													>
														×
													</Box>
												</Box>
											))}
											<Tooltip label={t('doodle.colors.add')} placement="top">
												<Box
													as="button"
													type="button"
													aria-label={t('doodle.colors.add')}
													width={rem(36)}
													height={rem(36)}
													p={rem(4)}
													borderRadius="full"
													border={0}
													bg="conic-gradient(#f04747, #fcc84f, #4bb475, #2981e6, #9d8df1, #f04747)"
													cursor="pointer"
													_hover={{ transform: 'translateY(-2px)' }}
													_focusVisible={{ outline: '2px solid', outlineColor: 'primary.fg', outlineOffset: rem(2) }}
													onClick={() => setIsCustomPickerOpen(true)}
												>
													<Flex
														width="100%"
														height="100%"
														borderRadius="full"
														bg="bg.surface"
														align="center"
														justify="center"
														color="text.default"
														fontSize="xl"
														lineHeight={1}
													>
														+
													</Flex>
												</Box>
											</Tooltip>
										</Box>
									)}
								</PopoverContent>
							</Portal>
						</>
					)}
				</Popover>

				<DoubleDivider />

				<HStack height={rem(72)} spacing={0} align="stretch" minW={0} flexShrink={1}>
					{DOODLE_SIZES.map((brushSize, index) => {
						const MarkerIcon = MARKER_ICONS[index];
						return (
							<DoodleToolButton
								key={brushSize.value}
								label={t(`doodle.sizes.${brushSize.name}`)}
								isActive={tool === 'brush' && size === brushSize.value}
								width={[44, 55, 58, 64][index]}
								onClick={() => {
									onSizeChange(brushSize.value);
									onToolChange('brush');
								}}
							>
								<MarkerIcon color={color} />
							</DoodleToolButton>
						);
					})}
					<Popover placement="top" isLazy isOpen={isEraserMenuOpen} onClose={() => setIsEraserMenuOpen(false)} returnFocusOnClose={false}>
						<PopoverTrigger>
							<ToolButtonBase
								label={t('doodle.eraser')}
								isActive={tool === 'eraser'}
								width={66}
								onClick={() => {
									onToolChange('eraser');
									setIsEraserMenuOpen(true);
								}}
							>
								<EraserIcon />
							</ToolButtonBase>
						</PopoverTrigger>
						<Portal containerRef={portalContainerRef}>
							<PopoverContent width="auto" p={2} bg="bg.surface" borderColor="border.soft" borderRadius="xl" boxShadow="xl" _focusVisible={{ boxShadow: 'xl' }}>
								<HStack spacing={2}>
									{DOODLE_ERASER_SIZES.map((eraserOption) => {
										const isSelected = tool === 'eraser' && eraserSize === eraserOption.value;
										return (
											<Tooltip key={eraserOption.value} label={t(`doodle.eraser_sizes.${eraserOption.name}`)} placement="top">
												<Flex
													as="button"
													type="button"
													aria-label={t(`doodle.eraser_sizes.${eraserOption.name}`)}
													aria-pressed={isSelected}
													align="center"
													justify="center"
													width={rem(44)}
													height={rem(44)}
													borderRadius="full"
													bg="transparent"
													borderWidth={rem(2)}
													borderStyle="solid"
													borderColor={isSelected ? 'primary.fg' : 'transparent'}
													cursor="pointer"
													transition="border-color 150ms ease"
													_hover={{ bg: 'secondary.bgAlpha' }}
													_focusVisible={{ outline: '2px solid', outlineColor: 'primary.fg', outlineOffset: rem(2) }}
													onClick={() => {
														onEraserSizeChange(eraserOption.value);
														onToolChange('eraser');
														setIsEraserMenuOpen(false);
													}}
												>
													<Box width={rem(eraserOption.dot)} height={rem(eraserOption.dot)} borderRadius="full" bg="text.default" />
												</Flex>
											</Tooltip>
										);
									})}
								</HStack>
							</PopoverContent>
						</Portal>
					</Popover>
				</HStack>
			</Flex>

			<HStack minHeight={rem(72)} spacing={{ base: 1, sm: 3 }} flexShrink={0} width={{ base: '100%', sm: 'auto' }} justify="flex-end">
				<DoubleDivider />
				<HStack spacing={0}>
					<Tooltip label={t('doodle.undo')}>
						<IconButton
							aria-label={t('doodle.undo')}
							icon={<UndoIcon />}
							variant="ghost"
							colorScheme="secondary"
							onClick={onUndo}
							isDisabled={!canUndo}
							width={{ base: rem(36), sm: rem(40) }}
							height={rem(51)}
							borderRadius="sm"
						/>
					</Tooltip>
					<Tooltip label={t('doodle.redo')}>
						<IconButton
							aria-label={t('doodle.redo')}
							icon={<RedoIcon />}
							variant="ghost"
							colorScheme="secondary"
							onClick={onRedo}
							isDisabled={!canRedo}
							width={{ base: rem(36), sm: rem(40) }}
							height={rem(51)}
							borderRadius="sm"
						/>
					</Tooltip>
				</HStack>
				<DoubleDivider />
				<HStack spacing={{ base: 1, sm: 2 }}>
					<Button
						variant="ghost"
						colorScheme="secondary"
						size="sm"
						height={{ base: rem(40), sm: rem(51) }}
						px={{ base: 3, sm: 4 }}
						fontSize={{ base: 'sm', sm: 'md' }}
						onClick={onCancel}
					>
						{t('doodle.cancel')}
					</Button>
					<Button
						colorScheme="primary"
						size="sm"
						height={{ base: rem(40), sm: rem(51) }}
						px={{ base: 3, sm: 4 }}
						fontSize={{ base: 'sm', sm: 'md' }}
						onClick={onSave}
						isDisabled={isSaveDisabled}
						isLoading={isSaving}
						loadingText={saveLabel}
						transition="transform 150ms cubic-bezier(0.16, 1, 0.3, 1), box-shadow 150ms cubic-bezier(0.16, 1, 0.3, 1)"
						_hover={{ transform: 'translateY(-1px)', boxShadow: 'md' }}
						_active={{ transform: 'scale(0.97)' }}
					>
						{saveLabel}
					</Button>
				</HStack>
			</HStack>
		</Flex>
	);
};
